import {
  effect,
  Injectable,
  makeStateKey,
  signal,
  StateKey,
  TransferState,
} from '@angular/core';
import { IMatchSession } from '../../../../server/interfaces/match-session';
import { config } from '../../../../shared/config';
import { SportType } from '../../../../shared/constants/sport-type';
import { ILocationCoordinate } from '../../../../shared/interfaces/location';
import { ApiResponse, HttpService } from './http.service';
import { NotificationService } from './notification.service';
import { SportFilterService } from './sport-filter.service';
import { PlatformService } from './platform.service';

@Injectable({
  providedIn: 'root',
})
export class MatchSessionService {
  filter = signal<string>('');
  lastFilter = signal<string>('');
  nearbyMatchSessions = signal<IMatchSession[]>([]);
  gettingNearbyMatchSessions = signal(false);
  endOfMatchSessionList = signal(false);
  cursor = signal<number>(Date.now());

  matchSession = signal<IMatchSession | null>(null);
  gettingMatchSession = signal<boolean>(false);

  initialized = signal(false);

  constructor(
    private http: HttpService,
    private sportFilterService: SportFilterService,
    private notification: NotificationService,
    private transferState: TransferState,
    private platform: PlatformService
  ) {}

  async getNearbyMatchSessions(
    sportType?: SportType | '',
    coordinates?: ILocationCoordinate,
    page?: number,
    resetCursor = false
  ) {
    // if (this.gettingNearbyMatchSessions()) return;
    // if (!coordinates) return;

    this.nearbyMatchSessions.set([]);
    this.endOfMatchSessionList.set(false);

    const stateKey: StateKey<number> = makeStateKey<number>(
      'transfer-get-nearby-match-sessions-cursor'
    );

    // If it's in TransferState (on client), use it and remove from TransferState
    if (this.transferState.hasKey(stateKey)) {
      const data = this.transferState.get<number>(stateKey, {} as number);
      this.transferState.remove(stateKey);
      this.cursor.set(data);
    } else if (resetCursor) this.cursor.set(Date.now());

    // Get nearby sessions
    const offset = (page ? page - 1 : 0) * config.dataTake;

    this.gettingNearbyMatchSessions.set(true);
    const res = await this.http.get<IMatchSession[]>(
      `/match-session/nearby-by-coordinates`,
      {
        lat: coordinates?.coordinates[1] ?? '',
        long: coordinates?.coordinates[0] ?? '',
        sportType: this.sportFilterService.filter(),
        offset,
        filter: this.filter(),
        cursor: this.cursor(),
      }
    );
    if (this.platform.isServer()) {
      this.transferState.set(stateKey, this.cursor());
    }
    this.gettingNearbyMatchSessions.set(false);

    if (res.error) {
      this.notification.error(res.error);
      return;
    }

    if (res.data) {
      this.nearbyMatchSessions.set(res.data);
      this.endOfMatchSessionList.set(res.data.length < config.dataTake);
    }

    return true;
  }

  async getMatchSession(id: string, coordinates?: ILocationCoordinate) {
    this.initialized.set(true);

    this.gettingMatchSession.set(true);

    let res;

    const stateKey: StateKey<ApiResponse<IMatchSession>> = makeStateKey<
      ApiResponse<IMatchSession>
    >(`transfer-get-match-session-${id}`);

    // If it's in TransferState (on client), use it and remove from TransferState
    if (this.transferState.hasKey(stateKey)) {
      const data = this.transferState.get<ApiResponse<IMatchSession>>(
        stateKey,
        {} as ApiResponse<IMatchSession>
      );
      res = data;
      this.transferState.remove(stateKey);
    } else {
      res = await this.http.get<IMatchSession>(
        `/match-session/get-with-coordinates/${id}`,
        {
          lat: coordinates?.coordinates[1] ?? '',
          long: coordinates?.coordinates[0] ?? '',
        }
      );
    }

    if (this.platform.isServer()) {
      this.transferState.set(stateKey, res);
    }

    this.gettingMatchSession.set(false);

    if (res.data) {
      this.matchSession.set(res.data);
    } else {
      this.matchSession.set(null);
      this.notification.error(res.error);
    }
  }

}
